import { useState, memo, useCallback, forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import { Field, FieldInputProps, FieldProps, FormikContextType, useField, useFormikContext } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { type AppState, type AppDispatch } from '@nextgen-web-framework/core';
import { FAIcon, useTranslation } from '@nextgen-web-framework/all';
import {
  DynamicObjectInterface,
  RegFlowConfig,
  updateRegistrationData,
  UsernameContactType,
} from '@commerce-profile-webcomponents/sdk';
import { OperationType } from '@commerce-identity-webcomponents/sdk';
import { UsernameField } from '@commerce-identity-webcomponents/ui';
import { APICallType, IdentityOnInputChangeCallbackResponse } from '../Registration/types/propTypes';
import { useCountryMasterData } from '../../utils/masterdata';
import { checkUserExists } from '../../utils/registrationUtils';
import { NUMBER_REGEX, VALIDATION_VALID_STRING } from '../../utils/Constants';
import SecondaryContactField from './SecondaryContactField';
import PasswordField from './PasswordField';
import styles from './RegistrationUsername.module.scss';
import {
  RegisterationPasswordRef,
  RegisterationUserNameRef,
  RegistrationUsernameProps,
  UsernameFormikContextValues,
} from './types/propTypes';

const RegistrationUsername = forwardRef<RegisterationUserNameRef, RegistrationUsernameProps>((props, ref) => {
  const {
    isPhonePrimaryContact,
    setIsPhonePrimaryContact,
    showPasswordField = true,
    showSecondaryContactField = true,
    isDrawerUsername = false,
    showOpsSecondaryField,
    wrappperClass,
    userNameLabel,
    nextHandler,
  } = props;

  const [identityChangedAfterPreview, setIdentityChangedAfterPreview] = useState<boolean>(isDrawerUsername);
  const dispatch = useDispatch<AppDispatch>();
  const t = useTranslation();
  const registrationFlowData = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const [, userNameMeta, userNameHandler] = useField('userName');
  const [, , emailHandler] = useField('email');
  const [, , phoneHandler] = useField('phone');
  const [, userNameValidatorMeta] = useField('userNameValidator');
  const [, , passwordHandler] = useField('password');
  const { values, status, setFieldValue, setStatus }: FormikContextType<UsernameFormikContextValues> =
    useFormikContext();
  const { OtpData, perosonalDetails, isRefreshRestoredData } = registrationFlowData;
  const { userName } = perosonalDetails;
  const { currentCountry } = useCountryMasterData();
  const defaultCountryDialCode = `+${currentCountry?.phoneCode}`;
  const isAboLedFlow = registrationFlowData?.flowName === RegFlowConfig.AboLedFlow;
  const patchVersion = registrationFlowData?.userData?.patchVersion;
  const passwordRef = useRef<RegisterationPasswordRef>(null);

  const onInputChangeCallback = useCallback(
    ({ amwayId, emailOrPhoneWithoutCC: inputValue }: IdentityOnInputChangeCallbackResponse) => {
      setIsPhonePrimaryContact(NUMBER_REGEX.test(inputValue as string));
      userNameHandler.setValue(inputValue ? amwayId : '');
      if (inputValue) {
        setFieldValue('userNameValidator', VALIDATION_VALID_STRING);
      }
    },
    [],
  );

  const setError = useCallback((err: string) => {
    err && setFieldValue('userNameValidator', err);
  }, []);

  useImperativeHandle(ref, () => ({
    validateUserNamePassword: () => {
      if (!values.userName && userNameValidatorMeta?.value !== VALIDATION_VALID_STRING) {
        setError('profile.validationMsg.userName.required');
      }
      passwordRef?.current?.validatePassword();
    },
    resetUserNamePassword: () => {
      setError('');
      passwordRef?.current?.resetPassword?.();
    },
  }));

  const checkDuplicateUser = useCallback(
    async (userName: string, field: FieldInputProps<DynamicObjectInterface>) => {
      try {
        setStatus({ ...status, apiValidating: true });
        const regPayload = {
          usernameContactType: isPhonePrimaryContact ? UsernameContactType.Phone : UsernameContactType.Email,
          version: patchVersion,
          [isPhonePrimaryContact ? 'phone' : 'email']: userNameMeta.value,
        };

        const regPatchHandler = () => nextHandler(regPayload, undefined, true, false, APICallType.PATCH);
        const res = await checkUserExists(userName, regPatchHandler);
        if (!res?.isUnique) {
          setFieldValue(
            'userNameValidator',
            res?.usageStatus
              ? `profile.RegistrationPersonalDetailsStep.checkUserMessage.${res.usageStatus}`
              : 'profile.RegistrationPersonalDetailsStep.duplicateUser',
          );
        } else {
          isPhonePrimaryContact ? phoneHandler.setValue(field.value) : emailHandler?.setValue(field.value);
        }
      } catch (error) {
        setFieldValue('userNameValidator', 'profile.common.errorHeading');
      } finally {
        setStatus({ ...status, apiValidating: false });
      }
    },
    [patchVersion, isPhonePrimaryContact, userNameMeta.value, nextHandler],
  );

  const getErrorUserNameMessage = () => {
    const { value, error } = userNameValidatorMeta || {};
    if (value && value !== VALIDATION_VALID_STRING) {
      return t(value);
    }
    if (error) {
      return t(error);
    }
    return '';
  };

  function getResetValue() {
    const resetEnable = values.userName || isDrawerUsername ? false : true;
    if (resetEnable) {
      emailHandler?.setValue('');
      phoneHandler?.setValue('');
      setFieldValue('userNameValidator', '');
      setFieldValue('secondaryContactValidator', '');
    }
    return !!resetEnable;
  }

  const userNameResetEnable = useMemo(() => getResetValue(), [values.userName, isDrawerUsername]);
  return (
    <>
      {/* --------email section--------- */}
      <div className={`${wrappperClass} ${styles['info-section']} ${isDrawerUsername && styles['drawer-section']}`}>
        <div className={styles['id-section']}>
          {!isDrawerUsername && (
            <div className={styles['id-section-header']}>
              <div className={styles['id-section-label']} id="username-heading">
                {userNameLabel ?? t('profile.RegistrationUsername.createAmwayId')}
              </div>
              {!identityChangedAfterPreview && isRefreshRestoredData && !isAboLedFlow && (
                <div className={styles['id-section-action']}>
                  <button
                    data-testid="edit-username"
                    onClick={() => {
                      setIdentityChangedAfterPreview((identityChaged) => !identityChaged);
                      dispatch(
                        updateRegistrationData({
                          OtpData: {
                            ...OtpData,
                            challengeId: '',
                            isOTPVerified: false,
                          },
                        }),
                      );
                      passwordHandler.setValue('');
                    }}
                  >
                    <FAIcon iconName={'pencil'} className={styles['input-icon']} />
                    <span className={styles['float-message']}>{t('profile.RegistrationUsername.editButtonText')}</span>
                  </button>
                </div>
              )}
            </div>
          )}
          <div
            className={`${wrappperClass ? styles['input-fullwidth-wrapper'] : styles['input-wrapper']} ${
              isDrawerUsername && styles['vertical-input']
            }`}
          >
            <div className={`${styles['input-fields']}`}>
              <Field type="hidden" name="userNameValidator" />
              <Field type="text" name="userName">
                {({ field, meta }: FieldProps) => (
                  <>
                    <UsernameField
                      error={getErrorUserNameMessage()}
                      setError={setError}
                      onInputChangeCallback={onInputChangeCallback}
                      dropDownDisableForSingleCountry={true}
                      onBlur={(value) => checkDuplicateUser(value, field)}
                      defaultLabel={t('profile.registration.username.smartLabel')}
                      mobileLabel={t('profile.registration.username.mobileText')}
                      emailLabel={t('profile.registration.username.emailText')}
                      checkUsernameConfig={OperationType.UPSERT}
                      disabled={
                        !status?.enableField ||
                        (isRefreshRestoredData && !identityChangedAfterPreview && !isAboLedFlow) ||
                        isDrawerUsername
                      }
                      autoPopulateValue={userName}
                      resetValues={userNameResetEnable}
                      forceError={
                        status?.enableField && meta.touched && userNameValidatorMeta?.value !== VALIDATION_VALID_STRING
                          ? true
                          : false
                      }
                    />
                  </>
                )}
              </Field>
            </div>
            <div className={styles['input-fields']}>
              {showPasswordField && (
                <PasswordField
                  isRefreshRestoredData={isRefreshRestoredData}
                  identityChangedAfterPreview={identityChangedAfterPreview}
                  ref={passwordRef}
                />
              )}
              {values.userName && showOpsSecondaryField && (
                <SecondaryContactField
                  isPhonePrimaryContact={isPhonePrimaryContact}
                  defaultCountryDialCode={defaultCountryDialCode}
                  nextHandler={nextHandler}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ----------secondary id section--------------- */}
      {showSecondaryContactField && (
        <div className={styles['info-section']}>
          <div className={styles['id-section']}>
            <div className={styles['id-section-label']}>
              {isPhonePrimaryContact
                ? t('profile.RegistrationUsername.provideEmail')
                : t('profile.RegistrationUsername.providePhone')}
              :
            </div>
            <div className={styles['input-wrapper']}>
              <div className={styles['input-fields']}>
                <SecondaryContactField
                  isPhonePrimaryContact={isPhonePrimaryContact}
                  defaultCountryDialCode={defaultCountryDialCode}
                  nextHandler={nextHandler}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

RegistrationUsername.displayName = 'RegistrationUsername';
export default memo(RegistrationUsername);
